import { firstRunApi } from "../lib/api";
import { Footer } from "../shell/Footer";
import { errText } from "../ui/useLoad";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOutEverywhere } from "../lib/supabase";
import { fuzzyFilter } from "../lib/fuzzy";
import { useMe, lastCompany } from "./useMe";
import { NewCompany } from "./NewCompany";
import { NotInstalled } from "../pages/NotInstalled";
import { useShortcuts } from "../lib/useShortcuts";
import { FirmsBox } from "../firms/FirmConsole";

// Select a company. Type to filter, arrows to move, Enter to open; Alt+N for a new company.
export function CompanyPicker() {
  const { me, error, loading, reload } = useMe();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [creating, setCreating] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const active = useMemo(() => (me?.companies ?? []).filter((c) => c.status === "active"), [me]);
  const list = useMemo(() => fuzzyFilter(active, q, (c) => c.name, 500), [active, q]);
  useEffect(() => {
    const last = lastCompany.get();
    const i = last ? list.findIndex((c) => c.id === last) : -1;
    setSel(q ? 0 : Math.max(i, 0));
  }, [list, q]);

  const open = (id: string) => { lastCompany.set(id); nav("/"); };
  useShortcuts([{ keys: "Alt+N", run: () => setCreating(true) }]);

  if (loading) return <main className="center muted">Loading…</main>;
  if (error?.notInstalled) return <NotInstalled reason={error.code} />;
  if (error) return <main className="center"><p className="msg err">{error.message}</p><button onClick={reload}>Try again</button></main>;
  if (creating || active.length === 0)
    return <NewCompany first={active.length === 0} onCancel={active.length ? () => setCreating(false) : undefined}
                       onCreated={(id) => { void reload(); open(id); }} extra={active.length === 0 ? <FirmsBox firms={me?.firms ?? []} reload={reload} /> : undefined} />;

  return (
    <main className="auth">
      <div className="card picker">
        <header className="row"><h1>Select company</h1><span className="muted small">{me?.email}</span></header>
        <input ref={input} autoFocus placeholder="Type to filter" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter companies"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, list.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
            else if (e.key === "Enter" && list[sel]) open(list[sel].id);
          }} />
        <ul className="list" role="listbox">
          {list.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === sel} className={i === sel ? "sel" : ""} onMouseEnter={() => setSel(i)} onClick={() => open(c.id)}>
              <span>{c.name}</span><span className="muted small">{c.role}{c.via ? ` via ${c.via}` : ""} · from {fmtDate(c.books_begin)}</span>
            </li>
          ))}
          {list.length === 0 && <li className="muted">No company matches “{q}”.</li>}
        </ul>
        <FirmsBox firms={me?.firms ?? []} reload={reload} />
        <footer className="row">
          <button onClick={() => setCreating(true)}>New company <kbd>Alt+N</kbd></button>
          <SampleButton onMade={(id) => { void reload(); open(id); }} />
          <a className="link" href="/account">Your account and plan</a>
          <button className="link" onClick={() => void signOutEverywhere()}>Sign out</button>
        </footer>
      </div>
      <Footer kind="public" />
    </main>
  );
}

export function fmtDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

// a practice company with two months of made-up trade (sql/239)
function SampleButton({ onMade }: { onMade: (id: string) => void }) {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  return (<>
    <button className="link" disabled={busy} title="A company with made-up figures to try things in" onClick={() => { setBusy(true); setErr(null);
      firstRunApi.sample().then(onMade).catch((e) => setErr(errText(e))).finally(() => setBusy(false)); }}>{busy ? "Making it…" : "Try a sample company"}</button>
    {err && <span className="msg err small">{err}</span>}
  </>);
}
